import { FC, useContext, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../context/notificationContext";
import { usePermissionsContext } from "../../../context/permissionContext";
import useToggle from "../../../hooks/useToggle";
import { useI18n } from "../../../hooks/useTranslations";
import useProcessingRequestApi from "../../../http/processingRequest/api";
import { ProcessingRequest, ProcessingRequestStatuses } from "../../../http/processingRequest/types";
import { formatDateLong, formatTimestampLong } from "../../../types/utils";
import SvgEdit from "../../icons/Edit";
import BlueCheck from "../../ui/BlueCheck";
import Box from "../../ui/Box";
import Inline from "../../ui/Inline";
import RedCross from "../../ui/RedCross";
import Sidebar from "../../ui/Sidebar";
import { Status } from "../../ui/Status";
import EditProcessingRequestForm, { ProcessingRequestFormBody } from "../EditProcessingRequestForm";
import UpdateAvailabilityDateForm from "../UpdateAvailabilityDateForm";
import UpdateStatusButton from "../UpdateStatusButton";
import styles from "./GeneralData.module.css";
import InfoCell from "./InfoCell";
import RequestDocuments from "./RequestDocuments";

type GeneralDataProps = {
  request: ProcessingRequest;
  trigger: () => {};
  totalUsedProductsCost: number;
};

const GeneralData: FC<GeneralDataProps> = ({ request, trigger, totalUsedProductsCost }) => {
  const dispatch = useContext(NotificationDispatch);
  const i18n = useI18n();
  const processingRequestApi = useProcessingRequestApi();
  const { isOpen, open, close } = useToggle();
  const permissions = usePermissionsContext();

  const [editAvailabilityDateOpen, setEditAvailabilityDateOpen] = useState<boolean>();

  const onSubmit = async (formData: ProcessingRequestFormBody) => {
    try {
      const buildCommon = {
        priorityId: formData.priority.id,
        supplierCode: formData.supplier?.code ?? null,
        costCenterCode: formData.costCenter?.code ?? null,
        pickingNumber: formData.pickingNumber ?? null,
        offerAcceptor: formData.offerAcceptor?.id ?? null,
      };
      await processingRequestApi.updateProcessingRequest(buildCommon, request?.id!);
      close();
      trigger();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const buildProcessingRequest = (requestData: ProcessingRequest): ProcessingRequestFormBody => {
    return {
      id: requestData?.id,
      priority: requestData.priority,
      supplier: requestData?.supplier ?? null,
      costCenter: requestData?.costCenter ?? null,
      pickingNumber: requestData?.pickingNumber ?? null,
      offerAcceptor: requestData?.offerAcceptor ?? null,
    };
  };

  const showEditWhenClosed = ProcessingRequestStatuses.Closed === request.status && permissions.editClosedRequests;

  return (
    <>
      <Sidebar isOpen={isOpen} onClose={close} title={i18n.t.processingRequests.editProcessingRequest}>
        <EditProcessingRequestForm
          offerMandatory={request.offerMandatory}
          type={request.processingType}
          processingRequest={buildProcessingRequest(request)}
          onSubmit={onSubmit}
        />
      </Sidebar>

      <Sidebar
        isOpen={editAvailabilityDateOpen}
        title={i18n.t.processingRequests.editAvailabilityDate}
        onClose={() => setEditAvailabilityDateOpen(false)}
      >
        <UpdateAvailabilityDateForm
          date={request.availabilityDate}
          onSubmit={async (body) => {
            if (body.date == request.availabilityDate) {
              setEditAvailabilityDateOpen(false);
              return;
            }
            try {
              await processingRequestApi.updateRequestAvailability({ availabliityDate: body.date || null }, request.id);
              trigger();
              setEditAvailabilityDateOpen(false);
            } catch (err) {
              dispatch(showErrorNotification(i18n, err));
            }
          }}
        />
      </Sidebar>
      <Box
        fullHeight
        type="secondary"
        title={i18n.t.common.generalData}
        onClick={ProcessingRequestStatuses.Open === request.status || showEditWhenClosed ? open : undefined}
        showEditButton={ProcessingRequestStatuses.Open === request.status || showEditWhenClosed}
      >
        <Inline className={styles.rowWrap} justifyContent="center">
          <div className={styles.infos}>
            <InfoCell
              title={i18n.t.common.createdAt}
              text={formatTimestampLong(request.createdAt, i18n.language) ?? "---"}
            />

            <InfoCell title={i18n.t.common.type} text={request.processingType.name ?? "---"} />

            <InfoCell title={i18n.t.common.priority} text={request.priority.name ?? "---"} />

            <InfoCell
              title={i18n.t.common.sendOfferTo}
              text={
                request.offerAcceptor ? request.offerAcceptor.firstName + " " + request.offerAcceptor.lastName : "---"
              }
            />

            <InfoCell
              title={i18n.t.common.availabilityDate}
              onClick={
                (request.status != ProcessingRequestStatuses.Closed && permissions.isWarehouseOperator) ||
                (request.status == ProcessingRequestStatuses.Closed && permissions.editClosedRequests)
                  ? () => setEditAvailabilityDateOpen(true)
                  : undefined
              }
              text={request.availabilityDate ? formatDateLong(request.availabilityDate, i18n.language) : "---"}
              sideIcon={
                request.status != ProcessingRequestStatuses.Closed ||
                (request.status == ProcessingRequestStatuses.Closed && permissions.editClosedRequests)
                  ? SvgEdit
                  : undefined
              }
            />

            <InfoCell title={i18n.t.common.author} text={request.author.firstName + " " + request.author.lastName} />

            <InfoCell
              title={i18n.t.costCenters.singular}
              text={request.costCenter ? `${request.costCenter.code} - ${request.costCenter.name}` : "---"}
            />

            <InfoCell
              title={i18n.t.common.productionPickingNumber}
              text={request.pickingNumber ? request.pickingNumber : "---"}
            />

            <InfoCell title={i18n.t.suppliers.singular} text={request.supplier ? request.supplier.name : "---"} />

            <RequestDocuments requestID={request.id}></RequestDocuments>

            <InfoCell title={i18n.t.common.offerMandatory} sideIcon={request.offerMandatory ? BlueCheck : RedCross} />
            <InfoCell title={i18n.t.common.isKit} sideIcon={request.isKit ? BlueCheck : RedCross} />
          </div>
          <div className={styles.centerContent}>
            <div className={styles.centerContent}>
              <strong>{i18n.t.common.status}:</strong>
              <Status status={request.status} />
            </div>

            <Inline flexWrap="nowrap">
              {request.status === ProcessingRequestStatuses.Open && permissions.isWarehouseOperator && (
                <UpdateStatusButton
                  request={request}
                  newStatus={ProcessingRequestStatuses.Accepted}
                  title={i18n.t.processingRequests.status.actions.takeInCharge}
                  onUpdate={trigger}
                />
              )}
              {(request.status === ProcessingRequestStatuses.Refused ||
                request.status === ProcessingRequestStatuses.Accepted) &&
                permissions.isWarehouseOperator &&
                request.offerMandatory && (
                  <UpdateStatusButton
                    request={request}
                    newStatus={ProcessingRequestStatuses.WaitingConfirmation}
                    title={i18n.t.processingRequests.status.actions.sendOffer}
                    onUpdate={trigger}
                  />
                )}
              {request.status === ProcessingRequestStatuses.WaitingConfirmation &&
                permissions.isPurchaseOffice &&
                request.offerMandatory && (
                  <UpdateStatusButton
                    request={request}
                    newStatus={ProcessingRequestStatuses.OfferApproved}
                    title={i18n.t.processingRequests.status.actions.accept}
                    totalUsedProductsCost={totalUsedProductsCost}
                    onUpdate={trigger}
                  ></UpdateStatusButton>
                )}
              {request.status === ProcessingRequestStatuses.WaitingConfirmation &&
                permissions.isPurchaseOffice &&
                request.offerMandatory && (
                  <UpdateStatusButton
                    request={request}
                    newStatus={ProcessingRequestStatuses.Refused}
                    title={i18n.t.processingRequests.status.actions.refuse}
                    onUpdate={trigger}
                  />
                )}
              {(request.status === ProcessingRequestStatuses.Refused ||
                request.status === ProcessingRequestStatuses.WaitingConfirmation) &&
                (permissions.isPurchaseOffice || permissions.isWarehouseOperator) &&
                request.offerMandatory && (
                  <UpdateStatusButton
                    request={request}
                    newStatus={ProcessingRequestStatuses.Discarded}
                    title={i18n.t.processingRequests.status.actions.discard}
                    onUpdate={trigger}
                  />
                )}
              {(request.status === ProcessingRequestStatuses.OfferApproved ||
                (!request.offerMandatory && request.status === ProcessingRequestStatuses.Accepted)) &&
                permissions.isWarehouseOperator && (
                  <UpdateStatusButton
                    request={request}
                    newStatus={ProcessingRequestStatuses.InProgress}
                    title={i18n.t.processingRequests.status.actions.start}
                    onUpdate={trigger}
                  />
                )}
              {request.status === ProcessingRequestStatuses.InProgress && permissions.isWarehouseOperator && (
                <UpdateStatusButton
                  request={request}
                  newStatus={ProcessingRequestStatuses.Closed}
                  title={i18n.t.processingRequests.status.actions.finishClose}
                  onUpdate={trigger}
                />
              )}
            </Inline>
          </div>
        </Inline>
      </Box>
    </>
  );
};

export default GeneralData;
